import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from "react";
import { Routes, useLocation, type Location } from "react-router-dom";
import { usePulseBackground } from "../pulse-background";
import "./page-transition.scss";

const wave_speed = 5600;

interface PageTransitionRoutesProps {
  children: ReactNode;
  durationMs?: number;
}

interface PageSlot {
  id: string;
  location: Location;
  isPending: boolean;
  origin?: { x: string; y: string };
}

function locationsMatch(a: Location, b: Location) {
  return (
    a.pathname === b.pathname &&
    a.search === b.search &&
    a.hash === b.hash
  );
}

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

interface PageTransitionSlotProps {
  page: PageSlot;
  durationMs?: number;
  onComplete: (id: string) => void;
  children: ReactNode;
}

function PageTransitionSlot({
  page,
  durationMs = wave_speed,
  onComplete,
  children,
}: PageTransitionSlotProps) {
  const { id, isPending, origin } = page;
  const revealRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(() => onComplete(id));

  useEffect(() => {
    onCompleteRef.current = () => onComplete(id);
  }, [id, onComplete]);

  useEffect(() => {
    if (!isPending || !origin) return;

    const el = revealRef.current;
    if (!el) return;

    let cancelled = false;

    const finish = () => {
      if (cancelled) return;
      el.classList.remove("is-revealing");
      el.classList.add("is-revealed");
      onCompleteRef.current();
    };

    const onEnd = (event: AnimationEvent) => {
      if (event.animationName !== "page-transition-reveal") return;
      el.removeEventListener("animationend", onEnd);
      finish();
    };

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reducedMotion) {
      el.classList.add("is-revealed");
      finish();
      return;
    }

    (async () => {
      el.classList.remove("is-revealing", "is-revealed");
      await waitForPaint();
      if (cancelled) return;

      void el.offsetWidth;
      el.classList.add("is-revealing");
      el.addEventListener("animationend", onEnd);
    })();

    return () => {
      cancelled = true;
      el.removeEventListener("animationend", onEnd);
    };
  }, [isPending, origin, durationMs]);

  const revealStyle = isPending && origin ? ({
    "--page-transition-origin-x": origin.x,
    "--page-transition-origin-y": origin.y,
    "--page-transition-duration": `${durationMs}ms`,
  } as CSSProperties) : undefined;

  const outerClassName = isPending ? "page-transition" : "page-transition-committed";
  const innerClassName = isPending ? "page-transition__reveal" : "page-transition__committed-content";

  return (
    <div className={outerClassName}>
      <div
        ref={revealRef}
        className={innerClassName}
        style={revealStyle}
      >
        {children}
      </div>
    </div>
  );
}

export function PageTransitionRoutes({
  children,
  durationMs = wave_speed,
}: PageTransitionRoutesProps) {
  const location = useLocation();
  const { spawn: spawnPulse } = usePulseBackground();
  const [pages, setPages] = useState<PageSlot[]>(() => [
    {
      id: location.key || "initial",
      location,
      isPending: false,
    },
  ]);

  const lastPointerRef = useRef({
    x: typeof window !== "undefined" ? window.innerWidth / 2 : 0,
    y: typeof window !== "undefined" ? window.innerHeight / 2 : 0,
  });

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement;
      if (target && typeof target.closest === "function") {
        const navFloating = target.closest(".nav-floating");
        if (navFloating) {
          const trigger = document.querySelector(".nav-trigger");
          if (trigger) {
            const rect = trigger.getBoundingClientRect();
            lastPointerRef.current = {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            };
            return;
          }
        }
      }

      lastPointerRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  const activePageLocationRef = useRef<Location>(location);
  const lastTargetLocationRef = useRef<Location>(location);

  useEffect(() => {
    if (locationsMatch(location, lastTargetLocationRef.current)) {
      return;
    }
    lastTargetLocationRef.current = location;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reducedMotion) {
      activePageLocationRef.current = location;
      setPages([
        {
          id: location.key || String(Date.now()),
          location,
          isPending: false,
        },
      ]);
      return;
    }

    const newOrigin = {
      x: `${lastPointerRef.current.x}px`,
      y: `${lastPointerRef.current.y}px`,
    };

// Spawn a background pulse wave using the same origin as the page transition
    spawnPulse({
        placement: "fullscreen",
        mode: "once",
        removeOnComplete: true,
        origin: newOrigin,
        zIndex: "calc(var(--z-top) - 1)",
        photoSelectionType: "nav",
        durationMs: durationMs,
    });

    setPages((prev) => {
      const lastPage = prev[prev.length - 1];
      const newPageSlot: PageSlot = {
        id: location.key || String(Date.now()),
        location,
        isPending: true,
        origin: newOrigin,
      };

      if (lastPage) {
        activePageLocationRef.current = lastPage.location;
        return [
          {
            ...lastPage,
            isPending: false,
          },
          newPageSlot,
        ];
      }
      return [newPageSlot];
    });
  }, [location, spawnPulse, durationMs]);

  const handleComplete = useCallback((completedId: string) => {
    setPages((prev) => {
      const completedPage = prev.find((p) => p.id === completedId);
      if (!completedPage) return prev;

      activePageLocationRef.current = completedPage.location;
      return [
        {
          ...completedPage,
          isPending: false,
        },
      ];
    });
  }, []);

  return (
    <>
      {pages.map((page) => (
        <PageTransitionSlot
          key={page.id}
          page={page}
          durationMs={durationMs}
          onComplete={handleComplete}
        >
          <Routes location={page.location}>{children}</Routes>
        </PageTransitionSlot>
      ))}
    </>
  );
}

